import React from "react";
import PropTypes from "prop-types";
// Material imports
import { CopyToClipboard, prototype } from "react-copy-to-clipboard";
import makeStyles from "@mui/styles/makeStyles";
import CopyReview from "@mui/icons-material/FileCopy";
import Button from "@mui/material/Button";

//Limitless imports
import StarRatingSystem from "@limitless/modern/components/star-ratings/StarRatings";
import InfoIcon from "@limitless/icons/InfoCircle";
import MultilineTextField from "@limitless/modern/inputs/multiline-text-field/MultilineTextField";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Grid from "@limitless/modern/layout/Grid";
import BinaryRatings from "@limitless/modern/components/binary-ratings/BinaryRatings";
import TextField from "@limitless/modern/inputs/text-field/TextField";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import { useNotificationContext } from "@limitless/common/hooks";
import { TextButton } from "@limitless/portal";
import OnboardingAnswerTypeSummary from "../conversation-review-criteria-panel/OnboardingAnswerTypeSummary";
import { calculateReviewSummaryPracticeQuestion } from "@limitless/portal";
import { useExpertMessageContext } from "../../../../../expert";
import { useWebSockets } from "@limitless/common/hooks/web-sockets/WebSocketsContext";
import { aiStar } from "@limitless/images";
import { useTranslation } from "@limitless/common/hooks";
const ConversationDetailedReviewFeedback = ({
  reviewCriteria = [],
  onCriteriaChange,
  reviewNotesEnabled,
  reviewNote = "",
  reviewNoteError,
  onNoteChange,
  disabled = false,
  message = {},
  messageId,
  onAIDraftChange,
  onFeedbackChange,
  feedback,
  isAIDraftSelected,
  isAIAssistEnabled,
  updateReviewCriteriaByAI,
  messageAiReview,
  showReviewCriteria
}) => {
  const classes = useStyles();
  const {
    addSuccessNotification
  } = useNotificationContext();
  const {
    passiveQrEventFlag,
    setPassiveQrEventFlag
  } = useWebSockets();
  const {
    t
  } = useTranslation();
  const reviewSummary = calculateReviewSummaryPracticeQuestion(message, t);
  const {
    fetchAIReview
  } = useExpertMessageContext();
  const [aiReview, setAiReview] = React.useState(null);
  const [aiReasoning, setAiReasoning] = React.useState({});
  React.useEffect(() => {
    if (messageAiReview !== null && messageAiReview !== void 0 && messageAiReview.createdAt) {
      setPassiveQrEventFlag(true);
    } else {
      setPassiveQrEventFlag(false);
    }
  }, [messageId]);
  const handleUseAIDraftClick = () => {
    const processAIReview = aiReview => {
      setAiReview(aiReview);
      const updatedReasoning = {};
      for (const criteria of reviewCriteria) {
        const matchingCriteria = aiReview.reviewCriteria.find(aiCriteria => aiCriteria.reviewCriteriaId === criteria.reviewCriteriaId);
        if (matchingCriteria) {
          criteria.score = matchingCriteria.score;
          updatedReasoning[criteria.reviewCriteriaId] = matchingCriteria.reasoning;
        }
      }
      updateReviewCriteriaByAI(aiReview);
      setAiReasoning(updatedReasoning);
      if (aiReview.reviewNotes) {
        onNoteChange(aiReview.reviewNotes || "");
      }
      onAIDraftChange(true);
    };
    if (messageAiReview !== null && messageAiReview !== void 0 && messageAiReview.createdAt) {
      processAIReview(messageAiReview);
    } else {
      fetchAIReview(messageId, processAIReview);
    }
  };
  return /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignContent: "flex-start",
    style: {
      paddingRight: 16
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    mt: 1
  }, /*#__PURE__*/React.createElement(OnboardingAnswerTypeSummary, {
    reviewSummary: reviewSummary,
    message: message
  })), showReviewCriteria && /*#__PURE__*/React.createElement(Typography, {
    variant: "subtitle2",
    className: classes.titleText
  }, "Please provide your feedback by using the form below. All review criteria have to be completed."), isAIAssistEnabled && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    display: "flex",
    justifyContent: "right",
    margin: "10px 0"
  }, /*#__PURE__*/React.createElement(Button, {
    id: "useAiDraftButton",
    variant: "contained",
    onClick: handleUseAIDraftClick,
    disabled: !passiveQrEventFlag
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: /*#__PURE__*/React.createElement("img", {
      src: aiStar,
      alt: "AI Star",
      style: {
        width: 30,
        height: 30,
        marginTop: "-40px",
        marginLeft: "-10px"
      }
    }),
    label: t("Save time by using AI to draft your review. You can assess it for accuracy and make changes before saving.")
  }), t("Use ai draft"))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, reviewCriteria.map((criteria, index) => {
    var _aiReview$reviewCrite, _aiReview$reviewCrite2;
    const {
      name,
      description,
      score,
      reviewScheme,
      reviewCriteriaId,
      optional,
      text
    } = criteria;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: index
    }, /*#__PURE__*/React.createElement(Grid, {
      container: true,
      justifyContent: "space-between",
      alignItems: "flex-start"
    }, /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 11
    }, /*#__PURE__*/React.createElement(Grid, {
      container: true,
      direction: "row"
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, `${index + 1}. ${name}`), optional && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      pl: "2px"
    }, " (optional) "))), description && /*#__PURE__*/React.createElement(IconButton, {
      icon: /*#__PURE__*/React.createElement(InfoIcon, null),
      label: description
    })), /*#__PURE__*/React.createElement(Grid, null, reviewScheme === "binary" ? /*#__PURE__*/React.createElement(Grid, {
      className: classes.radioButtonContainer
    }, /*#__PURE__*/React.createElement(BinaryRatings, {
      id: "quality-review-binary-ratings",
      value: score,
      onClick: score => onCriteriaChange(reviewCriteriaId, score),
      disabled: disabled,
      aiUpdated: isAIDraftSelected,
      reasoning: aiReasoning[reviewCriteriaId],
      aiGeneratedScore: aiReview === null || aiReview === void 0 ? void 0 : (_aiReview$reviewCrite = aiReview.reviewCriteria) === null || _aiReview$reviewCrite === void 0 ? void 0 : (_aiReview$reviewCrite2 = _aiReview$reviewCrite.find(c => c.reviewCriteriaId === reviewCriteriaId)) === null || _aiReview$reviewCrite2 === void 0 ? void 0 : _aiReview$reviewCrite2.score
    }), optional && !disabled && /*#__PURE__*/React.createElement(TextButton, {
      onClick: () => {
        onCriteriaChange(reviewCriteriaId, null);
      }
    }, "Clear")) : reviewScheme === "text-input" ? /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement(TextField, {
      id: "quality-review-text-input",
      placeholder: "Type here...",
      value: text || "",
      onChange: event => onCriteriaChange(reviewCriteriaId, null, event.target.value),
      disabled: disabled,
      sx: {
        "&.MuiInputBase-root": {
          marginTop: "10px !important",
          marginBottom: "10px !important"
        }
      }
    })) : /*#__PURE__*/React.createElement(Grid, {
      className: classes.starRatingContainer
    }, /*#__PURE__*/React.createElement(StarRatingSystem, {
      id: "quality-review-star-ratings",
      onClick: reviewScore => onCriteriaChange(reviewCriteriaId, optional && reviewScore === 1 && reviewScore === score ? null : reviewScore),
      maxScore: 5,
      defaultScore: 0,
      rating: score,
      disabled: disabled,
      aiUpdated: isAIDraftSelected,
      reasoning: aiReasoning[reviewCriteriaId]
    }))));
  }), reviewNotesEnabled && showReviewCriteria && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 11
  }, /*#__PURE__*/React.createElement(MultilineTextField, {
    id: "review-notes",
    label: "Guidance",
    onChange: event => onNoteChange(event.target.value),
    type: "textarea",
    multiline: true,
    rows: 8,
    value: reviewNote,
    fullWidth: true,
    placeholder: "Type here...",
    required: true,
    error: reviewNoteError !== "",
    errorText: reviewNoteError,
    disabled: disabled
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    item: true,
    xs: 1,
    style: {
      maxHeight: 32
    }
  }, /*#__PURE__*/React.createElement(CopyToClipboard, {
    key: "guidance",
    text: reviewNote,
    onCopy: () => {
      addSuccessNotification("Guidance copied to clipboard");
    }
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: /*#__PURE__*/React.createElement(CopyReview, null),
    label: "Copy guidance"
  })))), !showReviewCriteria && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center",
    mt: 2
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 11
  }, /*#__PURE__*/React.createElement(MultilineTextField, {
    id: "additional-review-notes",
    label: "Additional Feedback",
    onChange: event => onNoteChange(event.target.value),
    type: "textarea",
    rows: 8,
    fullWidth: true,
    placeholder: "Type here..."
  }))), isAIDraftSelected && /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 11
  }, /*#__PURE__*/React.createElement(MultilineTextField, {
    id: "ai-feedback",
    label: "AI Feedback",
    onChange: event => onFeedbackChange(event.target.value),
    type: "textarea",
    multiline: true,
    rows: 8,
    value: feedback,
    fullWidth: true,
    placeholder: "Please tell us why you changed the values",
    required: true,
    error: reviewNoteError !== "",
    errorText: reviewNoteError,
    disabled: disabled
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    item: true,
    xs: 1,
    style: {
      maxHeight: 32
    }
  }, /*#__PURE__*/React.createElement(CopyToClipboard, {
    key: "aiFeedback",
    text: feedback,
    onCopy: () => {
      addSuccessNotification("AI Feedback copied to clipboard");
    }
  }, /*#__PURE__*/React.createElement(IconButton, {
    icon: /*#__PURE__*/React.createElement(CopyReview, null),
    label: "Copy AI Feedback"
  }))))));
};
const useStyles = makeStyles(() => ({
  container: {
    height: 500,
    paddingBottom: 20,
    paddingRight: 8,
    paddinLeft: 8
  },
  starRatingContainer: {
    paddingBottom: 24,
    paddingTop: 16,
    paddingLeft: 10
  },
  radioButtonContainer: {
    paddingTop: 7,
    paddingBottom: 15,
    paddingLeft: 7,
    display: "flex",
    justifyContent: "space-between"
  },
  titleText: {}
}));
export default ConversationDetailedReviewFeedback;
ConversationDetailedReviewFeedback.propTypes = {
  reviewCriteria: PropTypes.array,
  onCriteriaChange: PropTypes.func,
  reviewNotesEnabled: PropTypes.bool,
  reviewNote: PropTypes.string,
  onNoteChange: PropTypes.func,
  onReviewCriteriaText: PropTypes.func,
  disabled: PropTypes.bool,
  reviewNoteError: PropTypes.string,
  message: PropTypes.object,
  messageId: PropTypes.string,
  onAIDraftChange: PropTypes.func,
  onFeedbackChange: PropTypes.func,
  isAIDraftSelected: PropTypes.bool,
  feedback: PropTypes.string,
  isAIAssistEnabled: PropTypes.bool,
  aiReviweCriteria: PropTypes.object,
  updateReviewCriteriaByAI: PropTypes.func,
  messageAiReview: PropTypes.object,
  orignalMessage: PropTypes.array,
  showReviewCriteria: PropTypes.bool
};